import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { AlertTriangle, CheckCircle, Clock } from 'lucide-react';

interface ConflictCheckProps {
  moduleIds: string[];
  scheduledDate: string;
  excludeDeploymentId?: string;
  onConflictsDetected?: (hasConflicts: boolean) => void;
}

interface Conflict {
  deploymentId: string;
  deploymentNumber: string;
  title: string;
  scheduledDate: string;
  status: string;
  conflictingModules: string[];
  requestedBy: {
    id: string;
    name: string;
  };
}

export const ConflictCheck: React.FC<ConflictCheckProps> = ({
  moduleIds,
  scheduledDate,
  excludeDeploymentId,
  onConflictsDetected
}) => {
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (moduleIds.length > 0 && scheduledDate) {
      checkConflicts();
    }
  }, [moduleIds, scheduledDate]);

  const checkConflicts = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/deployments/check-conflicts', {
        method: 'POST',
        body: JSON.stringify({
          moduleIds,
          scheduledDate,
          excludeDeploymentId
        })
      });

      const data = await response.json();
      setConflicts(data.conflicts || []);
      setHasChecked(true);
      
      if (onConflictsDetected) {
        onConflictsDetected(data.hasConflicts);
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-blue-600 text-sm">
        <Clock className="h-4 w-4 animate-spin" />
        Checking for conflicts...
      </div>
    );
  }

  if (!hasChecked) {
    return null;
  }

  if (conflicts.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
        <span className="text-green-800 font-medium">
          No conflicts detected. All modules are available for the selected time slot.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-amber-900 font-medium text-sm">
            {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} detected
          </p>
          <p className="text-amber-700 text-xs mt-1">
            The following deployments have overlapping modules in the same time window:
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {conflicts.map((conflict) => (
          <div
            key={conflict.deploymentId}
            className="p-3 bg-white border border-gray-200 rounded-lg hover:border-amber-300 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-gray-700">
                    #{conflict.deploymentNumber}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                    {conflict.status}
                  </span>
                </div>
                <p className="text-sm text-gray-900 mt-1">{conflict.title}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Requested by: {conflict.requestedBy.name}
                </p>
                <p className="text-xs text-gray-500">
                  Scheduled: {new Date(conflict.scheduledDate).toLocaleString()}
                </p>
              </div>
            </div>
            
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-600 mb-1">
                Conflicting Modules:
              </p>
              <div className="flex flex-wrap gap-1">
                {conflict.conflictingModules.map((moduleId, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded border border-red-200"
                  >
                    {moduleId}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        <AlertTriangle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Recommendation:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 text-blue-700">
            <li>Choose a different time slot</li>
            <li>Coordinate with other deployment requesters</li>
            <li>Remove conflicting modules from this deployment</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
